import { useState, useEffect } from 'react';

// Persist the advisor's Classic side-by-side choice across sessions.
const PAIR_VIEW_KEY = 'pq.present.pairSideBySide';
import { useStore } from '../store/StoreContext.jsx';
import { estateDocCodes } from '../store/estateDocs.js';
import { formatDollars, getDashboardMetrics, getIncludedIncomeRows, ownerPerson, getIncludedExpenseRows, getUnbookedRealEstateLoans, isRealEstateLoanRow, savingsRatioClass, dtiClass } from '../store/selectors.js';

/* =================================================================
   Present Mode — two views from one client record:
   - Financial Picture: visual executive snapshot (default)
   - Classic: the four-page Client Financial Summary
   Recreated from the Claude Design "Financial Picture" handoff and
   wired to live store data.
   ================================================================= */

const num = v => {
  if (v == null || v === '') return 0;
  const n = parseFloat(String(v).replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
};
const sumA = (arr, key) => (arr || []).filter(Boolean).reduce((s, r) => s + num(r[key]), 0);
const cleanRows = arr => (arr || []).filter(r =>
  r && Object.entries(r).some(([k, v]) => !k.startsWith('_') && v != null && String(v).trim() !== '')
);
const dash = v => (v == null || v === '') ? '—' : v;

// The other businesses from Employment, each as one more career line under the
// person who owns or works for it. A row naming neither person stays under the
// owner text it was given, or the client.
export function otherBusinessLines(ctx) {
  return cleanRows(ctx.emp.otherBusinesses)
    .filter(b => String(b.name ?? '').trim() || String(b.role ?? '').trim())
    .map(b => {
      const who = ownerPerson(ctx.family, b.owner);
      const owner = String(b.owner ?? '').trim();
      return {
        whoFull: who === 'client2' ? ctx.c2Name : who === 'client1' ? ctx.c1Name : (owner || ctx.c1Name),
        whoFirst: who === 'client2' ? (ctx.c2First || ctx.c2Name)
          : who === 'client1' ? (ctx.c1First || ctx.c1Name) : (owner || ctx.c1First || ctx.c1Name),
        role: String(b.role ?? '').trim(),
        name: String(b.name ?? '').trim(),
        years: String(b.numOfYears ?? '').trim(),
      };
    });
}
const pct = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);
const money0 = v => (num(v) ? formatDollars(num(v)) : '—');
// "Age 5" / "Ages 5, 8" — the value column shows ages, the label shows the
// count, so the two can't be misread for each other (QA: "Grandchildren (1)"
// with a value of "2" read like a contradiction).
const ageList = rows => {
  const ages = (rows || []).map(r => r && r.age).filter(v => v != null && String(v).trim() !== '');
  if (ages.length === 0) return '—';
  return (ages.length === 1 ? 'Age ' : 'Ages ') + ages.join(', ');
};

// Fonts (loaded in index.html)
const DISP = "'Space Grotesk',sans-serif";
const BODY = "'Source Sans 3',sans-serif";
const MONO = "'Space Grotesk',sans-serif"; // dot-free zeros (JetBrains Mono's dotted 0 read as 8 in QA)
const SERIF = "Georgia,'Times New Roman',serif";

// `dataOverride` renders a different store-shaped object than the live one —
// the planning workspace uses it to show the archived handoff snapshot through
// the same renderer the live record gets, which is what makes the archive
// viewable and printable without ever storing a document.
export default function PresentationScreen({ onBack, dataOverride }) {
  const { data: liveData } = useStore();
  const data = dataOverride || liveData;
  const m = getDashboardMetrics(data);
  // Default to Classic: advisors are instructed to present in the Classic view,
  // so opening there saves them a click (John, 7/10). The Financial Picture tab
  // is still one click away.
  const [view, setView] = useState('classic'); // 'picture' | 'classic'
  // Classic pages 1 & 2: stacked by default; advisors can toggle side-by-side.
  // The choice is remembered across sessions via localStorage.
  const [pairSideBySide, setPairSideBySide] = useState(() => {
    try { return localStorage.getItem(PAIR_VIEW_KEY) === 'true'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(PAIR_VIEW_KEY, String(pairSideBySide)); } catch { /* ignore */ }
  }, [pairSideBySide]);

  // Fit each Classic page to the printed sheet WIDTH. The pages are 1000px-wide
  // paper mockups; zoom (not transform) so the page-break layout scales with
  // it. beforeprint covers both the toolbar button and Ctrl+P; afterprint
  // restores the screen. The Financial Picture view has no .pres-page nodes
  // and is fitted by a constant zoom in presentation.css instead.
  useEffect(() => {
    // Letter at 96dpi. @page carries a 0.4in vertical margin (presentation.css),
    // so the usable height is 11in minus 0.8in; the full width is available
    // because the horizontal @page margin is 0 and each page's own side padding
    // supplies that margin instead.
    const SHEET_W = 816;
    const SHEET_H = 1056;
    const VERT_MARGIN = 0.4 * 96 * 2;
    // Four sheets falls out of the structure: four parts, each of which opens
    // a sheet. Jimmy, 2026-08-21: "keep this to 4 pages"; the Client Financial
    // Summary (2026-09-29) is those four pages, with no separate cover.
    const FIT_LEVELS = 6;

    // Does any of the four parts overrun its sheet at the current density?
    //
    // This asks about the PARTS, not a total page count, because each of the
    // four parts opens a sheet (see presentation.css). Four parts is four
    // sheets by construction; the only way to exceed the budget is for a part
    // to spill.
    //
    // Counting total sheets instead produced a blank page: a part whose box
    // ran a few pixels past the boundary emitted an empty fragment, the next
    // part's forced break then started a further sheet, and the reader got a
    // sheet with nothing on it at all. Fitting each part removes the spill
    // rather than counting the damage.
    const anyPartOverflows = zoom => {
      const usable = (SHEET_H - VERT_MARGIN) / zoom;
      let over = false;
      document.querySelectorAll('.pres-classic .pres-page').forEach(pageEl => {
        // Top/bottom padding is zeroed in print, so offsetHeight is content.
        if (pageEl.offsetHeight > usable) over = true;
      });
      return over;
    };

    const classicEl = () => document.querySelector('.pres-classic');

    const setLevel = level => {
      const el = classicEl();
      if (!el) return;
      for (let i = 1; i <= FIT_LEVELS; i++) el.classList.remove('fit-' + i);
      if (level > 0) el.classList.add('fit-' + level);
    };

    const fit = () => {
      document.querySelectorAll('.pres-page').forEach(el => {
        // Scale by WIDTH ONLY, so the 1000px design lands on an 816px sheet.
        // Height used to be in this ratio too, shrinking each page until its
        // whole contents fit one sheet — which made a household with ten
        // properties and six policies print at roughly half size, unreadable
        // (Jimmy, 2026-08-19). Height is now the print stylesheet's job:
        // sections are atomic blocks that flow onto as many sheets as they
        // need at full size, breaking only between sections.
        el.style.zoom = String(Math.min(1, SHEET_W / el.offsetWidth));
      });

      // Then tighten density, but only as far as the budget actually requires.
      // A household that already fits is left at full size.
      const first = document.querySelector('.pres-page');
      const zoom = first ? Math.min(1, SHEET_W / first.offsetWidth) : 1;
      const el = classicEl();
      if (el) el.classList.remove('fit-split');
      setLevel(0);
      let fits = !anyPartOverflows(zoom);
      for (let level = 1; level <= FIT_LEVELS && !fits; level++) {
        setLevel(level);
        fits = !anyPartOverflows(zoom);
      }
      // Type is as small as it goes and the document still runs long. What is
      // costing the page now is the gap an atomic section leaves when it will
      // not fit the remainder of a sheet, so let sections split. Tried last
      // because a section kept whole reads better than one carried over, and
      // shrinking type was what Jimmy asked for first.
      // A part that still overruns at the tightest density holds more than a
      // sheet can carry, and no amount of shrinking will change that. Let its
      // sections split so the overflow breaks cleanly onto the next sheet
      // instead of leaving a stray empty fragment behind. The document runs
      // long in that case, which is the honest outcome.
      if (!fits && el) el.classList.add('fit-split');
    };
    const restore = () => {
      document.querySelectorAll('.pres-page').forEach(el => { el.style.zoom = ''; });
      setLevel(0);
      const el = classicEl();
      if (el) el.classList.remove('fit-split');
    };
    window.addEventListener('beforeprint', fit);
    window.addEventListener('afterprint', restore);
    return () => {
      window.removeEventListener('beforeprint', fit);
      window.removeEventListener('afterprint', restore);
    };
  }, []);

  const family = data.family || {};
  const contact = data.contact || {};
  const emp = data.employment || {};
  const assets = data.assets || {};
  const liabs = data.liabilities || {};
  const ins = data.insurance || {};
  const income = data.income || {};
  const taxes = data.taxesExpenses || {};
  const rels = data.relationships || {};
  const epData = data.goals || {};
  const goalsText = (epData.clientGoals || '').trim();

  const c1First = (family.client1FirstName || '').trim();
  const c1Last = (family.client1LastName || '').trim();
  const c2First = (family.client2FirstName || '').trim();
  const c2Last = (family.client2LastName || '').trim();
  const c1Name = [c1First, c1Last].filter(Boolean).join(' ') || 'Client';
  const c2Name = [c2First, c2Last].filter(Boolean).join(' ');
  const clientDisplay = c2Name ? `${c1Name} & ${c2Name}` : c1Name;
  const cityState = [contact.city, contact.state].filter(Boolean).join(', ');
  const asOf = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  // Asset category totals
  const reAssets = sumA(assets.realEstate, 'marketValue');
  const busOther = sumA(assets.businessOther, 'marketValue');
  const taxDef = sumA(assets.taxDeferred, 'marketValue');
  const rothVal = sumA(assets.roth, 'marketValue');
  const taxableVal = sumA(assets.taxable, 'marketValue');
  const cashCd = sumA(assets.cashCd, 'marketValue');
  const plan529 = sumA(assets.plan529, 'marketValue');
  const hsaVal = sumA(assets.hsa, 'marketValue');

  // Income — only rows that are active now (excludes not-yet-claimed Social
  // Security and future-dated pension/other), taken from the shared selector so
  // the statement can never disagree with the edit rail. `incomeRows` also
  // drives which income rows the statements list, so totals and rows reconcile.
  const incomeRows = getIncludedIncomeRows(data);
  const totalIncome = m.totalIncome;

  // Tax
  const fedTax = num(taxes.federalTax);
  const stTax = num(taxes.stateTax);
  const ficaTax = num(taxes.ficaTax);
  const totalTax = fedTax + stTax + ficaTax;

  // Expenses / cash flow. Every cash-flow component comes from the shared engine
  // (getDashboardMetrics, `m`) so Present can never disagree with the Edit
  // dashboard — that divergence was the C1/C2 bug. debtPayments includes the
  // mortgage P&I; living counts insurance + manual rows once, never the
  // liability-sourced rows.
  const insNoneOf = Array.isArray(ins.noneOf) ? ins.noneOf.filter(Boolean) : [];
  const livingExpField = num(taxes.livingExpenses);
  const livingExpNote = String(taxes.livingExpensesNotes ?? '').trim();
  const expRows = (taxes.expenses || []).filter(Boolean);
  // Which of those rows count toward the CURRENT total. The Classic sheet is
  // the questionnaire record so every row is still listed (a 2028 college cost
  // is real client data the advisor typed); only the total is active-only,
  // exactly as the income tables already work.
  const activeExpKeys = new Set(getIncludedExpenseRows(data).map(r => r._uid || r));
  const debtPayments = m.liabilityPayments;
  const totalSavings = m.totalSavings;
  const livingExpAll = m.nonLiabilityExpenses;

  // Totals. Fold in any real-estate mortgage that isn't already itemized as a
  // liability so the client-facing Net Worth and equity can't be overstated
  // (these synthesized lines are also shown in the statements below).
  const reLoanLines = getUnbookedRealEstateLoans(assets, liabs.items);
  // Net-worth figures come straight from the shared engine so the client-facing
  // statement matches the edit rail exactly (m already folds in any unbooked
  // real-estate mortgage; reLoanLines is kept for the itemized display below).
  const totalAssets = m.totalAssets;
  const totalLiab = m.totalLiabilities;
  const netWorth = m.totalNetWorth;
  const totalOutflows = totalTax + totalSavings + debtPayments + livingExpAll;
  const netCF = totalIncome - totalOutflows;

  // Composition buckets (asset-composition donut sums to totalAssets).
  const investments = taxDef + rothVal + taxableVal;
  const cashEquiv = cashCd + plan529 + hsaVal + busOther;
  // Investable assets excludes real estate AND business/other, matching the
  // rail's definition (m.investableAssets) rather than folding business into
  // cash as the donut does.
  const investable = m.investableAssets;

  // Tax treatment mix
  const taxFreeTotal = rothVal + hsaVal;
  const taxableTotal = taxableVal + cashCd;
  const taxDeferredTotal = taxDef;

  // Ratios — from the shared engine / canonical helpers so the two views
  // agree: DTI is payments-based.
  const savingsRatio = m.savingsRatio;
  const dtiRatio = m.dti;
  const equityPct = pct(netWorth, totalAssets);
  const debtPct = pct(totalLiab, totalAssets);
  const cashReserveMonths = totalOutflows > 0 ? cashEquiv / (totalOutflows / 12) : 0;

  const childRows = (family.children || []).filter(c => c && (c.name || c.age));
  const grandRows = (family.grandchildren || []).filter(c => c && (c.name || c.age));
  // Advisors moving fast often fill only "# of Children" and never itemize
  // (the schema treats the count as a valid fallback), so both Present views
  // must fall back to the counts or entered family data silently vanishes.
  const numKids = childRows.length || Number(family.numChildren) || 0;
  // The typed number of grandchildren wins; named lines are optional extras.
  const numGrand = Math.max(grandRows.length, Number(family.numGrandchildren) || 0);

  const ctx = {
    data, family, contact, emp, assets, liabs, ins, income, taxes, rels, epData, goalsText,
    c1First, c1Last, c2First, c2Last, c2Name, c1Name, clientDisplay, cityState, asOf,
    reAssets, busOther, taxDef, rothVal, taxableVal, cashCd, plan529, hsaVal,
    incomeRows, totalIncome,
    fedTax, stTax, ficaTax, totalTax,
    livingExpField, livingExpNote, expRows, activeExpKeys, debtPayments, totalSavings, livingExpAll,
    insNoneOf,
    totalAssets, totalLiab, netWorth, totalOutflows, netCF, reLoanLines,
    investments, cashEquiv, investable,
    taxFreeTotal, taxableTotal, taxDeferredTotal,
    savingsRatio, dtiRatio, equityPct, debtPct, cashReserveMonths,
    childRows, grandRows, numKids, numGrand,
  };

  const tab = active => ({
    padding: '7px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
    font: `600 12px ${DISP}`,
    background: active ? '#fff' : 'transparent',
    color: active ? '#015697' : 'rgba(219,230,241,.8)',
  });

  return (
    <section className="screen is-active pres-screen">
      {/* nav — full width, sticky */}
      <div className="pres-topbar no-print" style={{ background: '#015697', padding: '14px 30px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
              <img src="/pure-logo.svg" alt="Pure Financial Advisors" style={{ height: 34, width: 'auto', display: 'block' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <button type="button" onClick={onBack} style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,.35)', background: 'transparent', color: '#dbe6f1', font: `600 12px ${DISP}`, cursor: 'pointer' }}>← Edit</button>
              <div style={{ display: 'flex', background: 'rgba(255,255,255,.1)', borderRadius: 8, padding: 3 }}>
                <button type="button" style={tab(view === 'picture')} onClick={() => setView('picture')}>Financial Picture</button>
                <button type="button" style={tab(view === 'classic')} onClick={() => setView('classic')}>Classic</button>
              </div>
              {view === 'classic' && (
                <button
                  type="button"
                  role="switch"
                  aria-checked={pairSideBySide}
                  onClick={() => setPairSideBySide(v => !v)}
                  title="Show the two Classic pages side by side (default is stacked)"
                  className={'pres-switch' + (pairSideBySide ? ' is-on' : '')}
                >
                  <span>Side by side</span>
                  <span className="pres-switch-track"><span className="pres-switch-thumb" /></span>
                </button>
              )}
              <button type="button" onClick={() => window.print()} style={{ padding: '8px 15px', borderRadius: 6, background: '#1a8fc4', color: '#fff', font: `600 12px ${DISP}`, border: 'none', cursor: 'pointer' }}>Print / Save PDF</button>
            </div>
          </div>

      {view === 'picture'
        ? <FinancialPictureView ctx={ctx} />
        : <ClassicView ctx={ctx} sideBySide={pairSideBySide} />}
    </section>
  );
}

/* ---------------- Shared visual helpers ---------------- */

function Donut({ segments }) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  let offset = 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <svg viewBox="0 0 100 100" style={{ width: 104, height: 104, flex: 'none', display: 'block' }}>
        <circle cx="50" cy="50" r="42" fill="none" stroke="#eef1f6" strokeWidth="14" />
        {segments.map((s, i) => {
          const len = (s.value / total) * 100;
          const el = (
            <circle key={i} cx="50" cy="50" r="42" fill="none" stroke={s.color}
              strokeWidth="14" pathLength="100"
              strokeDasharray={`${len} ${100 - len}`} strokeDashoffset={-offset}
              transform="rotate(-90 50 50)" />
          );
          offset += len;
          return el;
        })}
      </svg>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 9 }}>
        {segments.map((s, i) => (
          <div key={i} style={{ font: `500 12px ${BODY}`, color: '#4a5d7c' }}>
            <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: s.color, marginRight: 7 }} />
            {s.label} · {Math.round((s.value / total) * 100)}%
          </div>
        ))}
      </div>
    </div>
  );
}

// Compliance disclosure shown at the bottom of both views (and in the PDF —
// no no-print class, so it carries into print). `serif` matches the Classic
// view's Georgia type; default is the Financial Picture's sans.
const DISCLOSURE_LINES = [
  'Investment advisory and financial planning services are offered through Pure Financial Advisors, LLC, an SEC Registered Investment Advisor.',
  'This snapshot is based on client-provided information that has not been independently verified. Pure Financial Advisors assumes no liability for errors or omissions due to incomplete or inaccurate data.',
];

function Disclosure({ serif }) {
  const font = `400 10.5px ${serif ? SERIF : BODY}`;
  return (
    <div className="pres-disclosure" style={{ marginTop: 28, paddingTop: 16, borderTop: '1px solid rgba(10,26,56,.12)' }}>
      {DISCLOSURE_LINES.map((line, i) => (
        <p key={i} style={{ margin: i === 0 ? '0 0 8px' : 0, font, color: '#8a93a3', lineHeight: 1.55 }}>{line}</p>
      ))}
    </div>
  );
}

/* ======================================================
   FINANCIAL PICTURE VIEW
   ====================================================== */

function FinancialPictureView({ ctx }) {
  const c = ctx;
  const fmt = formatDollars;

  // Asset lines for the net-worth statement (scaled bars)
  const assetLines = [
    { label: 'Tax-Deferred Retirement', value: c.taxDef, color: '#1d4f99' },
    { label: 'Roth Accounts', value: c.rothVal, color: '#1a8fc4' },
    { label: 'Taxable Brokerage', value: c.taxableVal, color: '#2d6cc9' },
    { label: 'Cash & CDs', value: c.cashCd, color: '#2a8a8a' },
    { label: 'HSA & 529 College', value: c.hsaVal + c.plan529, color: '#3a9bd1' },
    ...cleanRows(c.assets.realEstate).map(r => ({ label: r.description || 'Real Estate', value: num(r.marketValue), color: '#5b6b86' })),
    ...cleanRows(c.assets.businessOther).map(r => ({ label: r.description || 'Business / Other', value: num(r.marketValue), color: '#8a5fcf' })),
  ].filter(l => l.value > 0);
  const maxLine = assetLines.reduce((mx, l) => Math.max(mx, l.value), 0) || 1;

  const liabLines = [
    ...cleanRows(c.liabs.items).map(r => ({ label: r.description || 'Liability', value: num(r.amount) })),
    ...c.reLoanLines.map(l => ({ label: l.description, value: l.amount })),
  ];

  // Cash-flow inflows. Built from the same included-income rows that make up
  // Gross Income, so the listed inflows always sum to the total shown (a
  // not-yet-claimed Social Security row is excluded from both).
  const inflowRows = [
    ...cleanRows(c.incomeRows.employment).map(r => ({ label: [r.owner, r.description || 'Salary'].filter(Boolean).join(' — '), value: num(r.annualAmount) })),
    ...cleanRows(c.incomeRows.socialSecurity).map(r => ({ label: [r.owner, 'Social Security'].filter(Boolean).join(' — '), value: num(r.annualAmount) })),
    ...cleanRows(c.incomeRows.pension).map(r => ({ label: [r.owner, r.description || 'Pension'].filter(Boolean).join(' — '), value: num(r.annualAmount) })),
    ...cleanRows(c.incomeRows.other).map(r => ({ label: r.description || r.owner || 'Other Income', value: num(r.annualAmount) })),
  ].filter(l => l.value > 0);

  const outflowRows = [
    { label: 'Income & Payroll Taxes', value: c.totalTax },
    { label: 'Savings & Contributions', value: c.totalSavings },
    { label: 'Debt Payments', value: c.debtPayments },
    { label: 'Living Expenses', value: c.livingExpAll },
  ].filter(l => l.value > 0);

  // Income allocation segments
  const allocColors = { Taxes: '#3a4a66', Living: '#5b6b86', Debt: '#8693a8', Savings: '#1a8fc4', 'Free Cash': '#1f7a4d' };
  const allocSegs = [
    { label: 'Taxes', value: c.totalTax },
    { label: 'Living', value: c.livingExpAll },
    { label: 'Debt', value: c.debtPayments },
    { label: 'Savings', value: c.totalSavings },
    { label: 'Free Cash', value: Math.max(c.netCF, 0) },
  ].filter(s => s.value > 0);
  const allocTotal = allocSegs.reduce((s, x) => s + x.value, 0) || 1;

  const heroKpi = (label, value, sub, color) => (
    <div>
      <div style={{ font: `600 9.5px ${DISP}`, letterSpacing: '.13em', textTransform: 'uppercase', color: '#7da6c9' }}>{label}</div>
      <div style={{ font: `600 23px ${MONO}`, letterSpacing: '-.02em', marginTop: 6, color: color || '#fff' }}>{value}</div>
      <div style={{ fontSize: 11, color: '#9fbdd6', marginTop: 4 }}>{sub}</div>
    </div>
  );

  const colTitle = { font: `600 13px ${DISP}`, color: '#0a1a38', paddingBottom: 8, borderBottom: '1px solid #0a1a38', marginBottom: 10 };
  const eyebrow = { font: `600 11px ${DISP}`, letterSpacing: '.18em', textTransform: 'uppercase', color: '#0f6f99' };
  const cardTitle = { font: `600 14px ${DISP}`, color: '#0a1a38', letterSpacing: '-.01em', marginBottom: 16 };

  return (
    <div className="pres-fp" style={{ background: '#fcfcfa', fontFamily: BODY, minHeight: '100%' }}>
      <div className="pres-fp-sheet" style={{ maxWidth: 1280, margin: '0 auto', padding: '44px 60px 52px' }}>

      {/* masthead */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: 20, borderBottom: '2px solid #0a1a38' }}>
        <div>
          <div style={{ font: `600 11px ${DISP}`, letterSpacing: '.2em', textTransform: 'uppercase', color: '#0f6f99' }}>Personal Financial Statement</div>
          <div style={{ font: `600 30px ${DISP}`, color: '#0a1a38', letterSpacing: '-.02em', marginTop: 8 }}>{c.clientDisplay}</div>
        </div>
        <div style={{ textAlign: 'right', font: `500 12px ${BODY}`, color: '#748097', lineHeight: 1.6, paddingTop: 6 }}>
          As of {c.asOf}{c.cityState ? <><br />{c.cityState}</> : null}
        </div>
      </div>

      {/* navy hero band + KPI strip */}
      <div style={{ background: 'linear-gradient(120deg,#0a1a38,#15315a)', borderRadius: 16, padding: '28px 34px', marginTop: 28, display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 36, alignItems: 'center', color: '#fff' }}>
        <div style={{ borderRight: '1px solid rgba(255,255,255,.14)', paddingRight: 32 }}>
          <div style={{ font: `600 11px ${DISP}`, letterSpacing: '.18em', textTransform: 'uppercase', color: '#7da6c9' }}>Total Net Worth</div>
          <div style={{ font: `600 54px ${MONO}`, letterSpacing: '-.03em', marginTop: 6, lineHeight: 1 }}>{fmt(c.netWorth)}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center', fontSize: 12.5, color: '#9fbdd6', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: '#36b5e0' }} />Assets {fmt(c.totalAssets)}
            <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: '#c0533f', marginLeft: 10 }} />Liabilities {fmt(c.totalLiab)}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24 }}>
          {heroKpi('Investable Assets', fmt(c.investable), `${Math.round(pct(c.investable, c.totalAssets))}% of assets`)}
          {heroKpi('Gross Income', fmt(c.totalIncome), 'household, annual')}
          {heroKpi('Net Cash Flow', (c.netCF >= 0 ? '+' : '') + fmt(c.netCF), c.netCF >= 0 ? 'annual surplus' : 'annual shortfall', c.netCF >= 0 ? '#7fe0ab' : '#ff9b8a')}
        </div>
      </div>

      {/* statement of net worth */}
      <div style={{ ...eyebrow, margin: '36px 0 18px' }}>Statement of Net Worth</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56 }}>
        {/* assets */}
        <div>
          <div style={colTitle}>Assets</div>
          {assetLines.map((l, i) => (
            <div key={i} style={{ padding: '8px 0 9px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <span style={{ font: `500 14px ${BODY}`, color: '#1f3354' }}>{l.label}</span>
                <span style={{ font: `500 14px ${MONO}`, color: '#0b1b36' }}>{fmt(l.value)}</span>
              </div>
              <div style={{ height: 5, borderRadius: 999, background: '#eef1f6' }}>
                <div style={{ height: '100%', width: `${pct(l.value, maxLine)}%`, background: l.color, borderRadius: 999 }} />
              </div>
            </div>
          ))}
          {assetLines.length === 0 && <div style={{ font: `400 13px ${BODY}`, color: '#9ca6b8', padding: '8px 0' }}>No assets recorded.</div>}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0 0', borderTop: '2px solid #0a1a38', marginTop: 8, font: `600 14px ${DISP}`, color: '#0a1a38' }}>
            <span>Total Assets</span><span style={{ font: `600 15px ${MONO}`, color: '#1f7a4d' }}>{fmt(c.totalAssets)}</span>
          </div>
        </div>
        {/* liabilities + equity + net worth */}
        <div>
          <div style={colTitle}>Liabilities</div>
          {liabLines.map((l, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #f0f0ec', font: `500 14px ${BODY}`, color: '#1f3354' }}>
              <span>{l.label}</span><span style={{ font: `500 14px ${MONO}`, color: '#0b1b36' }}>{fmt(l.value)}</span>
            </div>
          ))}
          {liabLines.length === 0 && <div style={{ font: `400 13px ${BODY}`, color: '#9ca6b8', padding: '9px 0' }}>No liabilities recorded.</div>}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0 0', borderTop: '2px solid #0a1a38', marginTop: 8, font: `600 14px ${DISP}`, color: '#0a1a38' }}>
            <span>Total Liabilities</span><span style={{ font: `600 15px ${MONO}`, color: '#b3382b' }}>{fmt(c.totalLiab)}</span>
          </div>

          <div style={{ marginTop: 26, padding: '18px 20px', background: '#fff', border: '1px solid #e8ecf2', borderRadius: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ font: `600 10px ${DISP}`, letterSpacing: '.13em', textTransform: 'uppercase', color: '#4a5d7c' }}>Equity Position</span>
              <span style={{ font: `600 12px ${MONO}`, color: '#0f6f99' }}>{Math.round(c.equityPct)}% of assets</span>
            </div>
            <div style={{ height: 12, borderRadius: 6, background: '#dde3ec', display: 'flex', overflow: 'hidden' }}>
              <div style={{ width: `${c.equityPct}%`, background: 'linear-gradient(90deg,#0f6f99,#1a8fc4)', height: '100%' }} />
              <div style={{ width: `${c.debtPct}%`, background: '#b3382b', height: '100%' }} />
            </div>
          </div>

          <div style={{ marginTop: 18, padding: '22px 24px', background: '#0a1a38', borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ font: `600 11px ${DISP}`, letterSpacing: '.16em', textTransform: 'uppercase', color: '#7da6c9' }}>Net Worth</span>
            <span style={{ font: `600 28px ${MONO}`, color: '#fff', letterSpacing: '-.02em' }}>{fmt(c.netWorth)}</span>
          </div>
        </div>
      </div>

      {/* visual band */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 18, marginTop: 36 }}>
        <div className="fp-card" style={{ border: '1px solid #e8ecf2', borderRadius: 14, padding: '22px 24px', background: '#fff' }}>
          <div style={cardTitle}>Asset Composition</div>
          <Donut segments={[
            { label: 'Investments', value: c.investments, color: '#0f6f99' },
            { label: 'Real Estate', value: c.reAssets, color: '#5b6b86' },
            { label: 'Cash & Equiv', value: c.cashEquiv, color: '#2a8a8a' },
          ].filter(s => s.value > 0)} />
        </div>
        <div className="fp-card" style={{ border: '1px solid #e8ecf2', borderRadius: 14, padding: '22px 24px', background: '#fff' }}>
          <div style={cardTitle}>Tax Treatment Mix</div>
          <Donut segments={[
            { label: 'Tax-Deferred', value: c.taxDeferredTotal, color: '#1d4f99' },
            { label: 'Taxable', value: c.taxableTotal, color: '#6e4ab8' },
            { label: 'Tax-Free', value: c.taxFreeTotal, color: '#1a8fc4' },
          ].filter(s => s.value > 0)} />
        </div>
        <div className="fp-card" style={{ border: '1px solid #e8ecf2', borderRadius: 14, padding: '22px 24px', background: '#fff' }}>
          <div style={cardTitle}>Key Ratios</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 18px' }}>
            <Ratio label="Savings Rate" value={c.savingsRatio.toFixed(1) + '%'} status={savingsRatioClass(c.savingsRatio)} />
            <Ratio label="Debt-to-Income" value={c.dtiRatio.toFixed(1) + '%'} status={dtiClass(c.dtiRatio)} />
            <Ratio label="Cash Reserve" value={c.cashReserveMonths.toFixed(1) + ' mo'} />
          </div>
        </div>
      </div>

      {/* statement of cash flow */}
      <div className="fp-cashflow-head" style={{ ...eyebrow, margin: '40px 0 18px' }}>Statement of Cash Flow · Annual</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56 }}>
        <div>
          <div style={{ font: `600 13px ${DISP}`, color: '#1f7a4d', paddingBottom: 8, borderBottom: '1px solid #cfe3d8', marginBottom: 6 }}>Inflows</div>
          {inflowRows.map((l, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #f0f0ec', font: `500 14px ${BODY}`, color: '#1f3354' }}>
              <span>{l.label}</span><span style={{ font: `500 14px ${MONO}`, color: '#0b1b36' }}>{fmt(l.value)}</span>
            </div>
          ))}
          {inflowRows.length === 0 && <div style={{ font: `400 13px ${BODY}`, color: '#9ca6b8', padding: '9px 0' }}>No income recorded.</div>}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0 0', borderTop: '2px solid #1f7a4d', marginTop: 6, font: `600 14px ${DISP}`, color: '#0a1a38' }}>
            <span>Gross Income</span><span style={{ font: `600 15px ${MONO}`, color: '#1f7a4d' }}>{fmt(c.totalIncome)}</span>
          </div>
        </div>
        <div>
          <div style={{ font: `600 13px ${DISP}`, color: '#b3382b', paddingBottom: 8, borderBottom: '1px solid #ecd4d0', marginBottom: 6 }}>Outflows</div>
          {outflowRows.map((l, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #f0f0ec', font: `500 14px ${BODY}`, color: '#1f3354' }}>
              <span>{l.label}</span><span style={{ font: `500 14px ${MONO}`, color: '#0b1b36' }}>{fmt(l.value)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0 0', borderTop: '2px solid #b3382b', marginTop: 6, font: `600 14px ${DISP}`, color: '#0a1a38' }}>
            <span>Total Outflows</span><span style={{ font: `600 15px ${MONO}`, color: '#b3382b' }}>{fmt(c.totalOutflows)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 14px', background: '#0a1a38', borderRadius: 9, marginTop: 14, font: `600 13px ${DISP}`, color: '#fff' }}>
            <span>Net Cash Flow</span><span style={{ font: `600 16px ${MONO}`, color: c.netCF >= 0 ? '#7fe0ab' : '#ff9b8a' }}>{(c.netCF >= 0 ? '+' : '') + fmt(c.netCF)}</span>
          </div>
        </div>
      </div>

      {/* income allocation flow */}
      {allocSegs.length > 0 && (
        <div className="fp-card" style={{ marginTop: 26, border: '1px solid #e8ecf2', borderRadius: 14, padding: '24px 26px', background: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <div style={cardTitle}>How Every Dollar Is Used</div>
            <div style={{ font: `500 12.5px ${BODY}`, color: '#748097' }}>of <span style={{ font: `600 12.5px ${MONO}`, color: '#0a1a38' }}>{fmt(c.totalIncome)}</span> gross income</div>
          </div>
          <div style={{ font: `400 12.5px ${BODY}`, color: '#748097', marginBottom: 18 }}>Taxes and fixed commitments come out first; what remains is free cash flow.</div>
          <div style={{ display: 'flex', height: 46, borderRadius: 9, overflow: 'hidden', boxShadow: 'inset 0 0 0 1px rgba(10,26,56,.05)' }}>
            {allocSegs.map((s, i) => {
              const p = pct(s.value, allocTotal);
              return (
                <div key={i} style={{ width: `${p}%`, background: allocColors[s.label], display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0, overflow: 'hidden' }}>
                  {p >= 8 && <span style={{ font: `600 12px ${MONO}`, color: '#fff' }}>{Math.round(p)}%</span>}
                </div>
              );
            })}
          </div>
          {/* Wrapping legend — independent of segment widths so labels never overlap */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px', marginTop: 14 }}>
            {allocSegs.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: allocColors[s.label], alignSelf: 'center', flex: 'none' }} />
                <span style={{ font: `600 10px ${DISP}`, letterSpacing: '.08em', textTransform: 'uppercase', color: s.label === 'Free Cash' ? '#1f7a4d' : '#4a5d7c' }}>{s.label}</span>
                <span style={{ font: `600 14px ${MONO}`, color: s.label === 'Free Cash' ? '#1f7a4d' : '#0a1a38' }}>{fmt(s.value)}</span>
                <span style={{ font: `500 11px ${BODY}`, color: '#9ca6b8' }}>{Math.round(pct(s.value, allocTotal))}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* client profile */}
      <ClientProfile ctx={c} />

      {/* disclosure */}
      <Disclosure />

      </div>
    </div>
  );
}

// status comes from the shared threshold selectors so the verdict colors here
// always match the edit rail. No status = neutral ink (informational ratios).
const RATIO_STATUS_COLORS = { good: '#1f7a4d', warn: '#b87514', bad: '#b3382b' };

function Ratio({ label, value, status }) {
  return (
    <div>
      <div style={{ font: `600 9.5px ${DISP}`, letterSpacing: '.1em', textTransform: 'uppercase', color: '#748097' }}>{label}</div>
      <div style={{ font: `600 19px ${MONO}`, color: RATIO_STATUS_COLORS[status] || '#0b1b36', marginTop: 3 }}>{value}</div>
    </div>
  );
}

function ClientProfile({ ctx: c }) {
  const head = { font: `600 10px ${DISP}`, letterSpacing: '.14em', textTransform: 'uppercase', color: '#0f6f99', paddingBottom: 8, borderBottom: '1px solid #e8ecf2', marginBottom: 12 };
  const row = { display: 'flex', justifyContent: 'space-between', padding: '5px 0', font: `400 13.5px ${BODY}`, color: '#1f3354' };
  const muted = { color: '#748097' };

  const goals = c.goalsText
    ? c.goalsText.split(/\r?\n+/).map(s => s.trim()).filter(Boolean)
    : [];

  const careers = [];
  if (c.emp.client1 && (c.emp.client1.jobTitle || c.emp.client1.employer)) {
    careers.push({ who: c.c1Name, job: [c.emp.client1.jobTitle, c.emp.client1.employer].filter(Boolean).join(', '), when: yearOf(c.emp.client1.retirementDate) });
  }
  if (c.c2Name && c.emp.client2 && (c.emp.client2.jobTitle || c.emp.client2.employer)) {
    careers.push({ who: c.c2Name, job: [c.emp.client2.jobTitle, c.emp.client2.employer].filter(Boolean).join(', '), when: yearOf(c.emp.client2.retirementDate) });
  }
  for (const b of otherBusinessLines(c)) {
    careers.push({ who: b.whoFull, job: [b.role, b.name].filter(Boolean).join(', '), when: '' });
  }

  const lifeBenefit = sumA((c.ins.policies || []).filter(p => /life/i.test(p?.type || '')), 'benefit');
  const ltc = (c.ins.policies || []).find(p => /ltc|long/i.test(p?.type || ''));
  const estate = Array.isArray(c.epData.estatePlan) ? c.epData.estatePlan.join(' + ') : (c.epData.estatePlan || '');
  const proRows = cleanRows(c.rels.professionals);

  const marriedNote = c.family.yearsMarried ? `Married ${c.family.yearsMarried} years` : '';
  const profileSub = [marriedNote, c.cityState].filter(Boolean).join(' · ');

  return (
    <div style={{ marginTop: 26, border: '1px solid #e8ecf2', borderRadius: 14, padding: '24px 26px 26px', background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18 }}>
        <div style={{ font: `600 14px ${DISP}`, color: '#0a1a38', letterSpacing: '-.01em' }}>Client Profile</div>
        {profileSub && <div style={{ font: `500 12.5px ${BODY}`, color: '#748097' }}>{profileSub}</div>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '22px 40px' }}>
        {/* Household */}
        <div>
          <div style={head}>Household</div>
          <div style={row}><span>{c.c1Name}</span><span style={muted}>{c.family.client1Age ? 'Age ' + c.family.client1Age : '—'}</span></div>
          {c.c2Name && <div style={row}><span>{c.c2Name}</span><span style={muted}>{c.family.client2Age ? 'Age ' + c.family.client2Age : '—'}</span></div>}
          {c.numKids > 0 && <div style={row}><span>Children ({c.numKids})</span><span style={muted}>{ageList(c.childRows) || '—'}</span></div>}
          {c.numGrand > 0 && <div style={row}><span>Grandchildren ({c.numGrand})</span><span style={muted}>{ageList(c.grandRows) || '—'}</span></div>}
        </div>
        {/* Careers & Retirement */}
        <div>
          <div style={head}>Careers & Retirement</div>
          {careers.length > 0 ? careers.map((cr, i) => (
            <div key={i} style={{ padding: '5px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', font: `500 13.5px ${BODY}`, color: '#1f3354', gap: 12 }}>
                <span>{cr.who}{cr.job ? ' — ' + cr.job : ''}</span>
                <span style={{ font: `600 13px ${MONO}`, color: '#0a1a38', flex: 'none' }}>{cr.when || '—'}</span>
              </div>
            </div>
          )) : <div style={{ font: `400 12px ${BODY}`, color: '#9ca6b8' }}>No occupation recorded.</div>}
        </div>
        {/* Planning Goals */}
        <div>
          <div style={head}>Planning Goals</div>
          {goals.length > 0 ? goals.map((g, i) => (
            <div key={i} style={{ display: 'flex', gap: 9, padding: '4px 0', font: `400 13.5px ${BODY}`, color: '#1f3354', lineHeight: 1.45 }}>
              <span style={{ color: '#1a8fc4', flex: 'none' }}>▪</span><span>{g}</span>
            </div>
          )) : <div style={{ font: `400 12px ${BODY}`, color: '#9ca6b8' }}>No goals recorded.</div>}
        </div>
        {/* Protection & Advisory */}
        <div>
          <div style={head}>Protection & Advisory Team</div>
          {lifeBenefit > 0 && <div style={row}><span>Life insurance coverage</span><span style={{ font: `600 13px ${MONO}`, color: '#0a1a38' }}>{formatDollars(lifeBenefit)}</span></div>}
          {ltc && <div style={row}><span>Long-term care</span><span style={muted}>{money0(ltc.benefit)}</span></div>}
          {estate && <div style={row}><span>Estate documents</span><span style={{ color: '#1f7a4d', fontWeight: 600 }}>{estate}{c.epData.yearsEstablished ? ' · ' + c.epData.yearsEstablished : ''}</span></div>}
          {proRows.map((p, i) => (
            <div key={i} style={row}><span>{p.role || 'Advisor'}</span><span style={muted}>{p.name || p.firm || '—'}</span></div>
          ))}
          {lifeBenefit === 0 && !ltc && !estate && proRows.length === 0 && <div style={{ font: `400 12px ${BODY}`, color: '#9ca6b8' }}>None recorded.</div>}
        </div>
      </div>
    </div>
  );
}

function yearOf(dateStr) {
  if (!dateStr) return '';
  const m = /(\d{4})/.exec(String(dateStr));
  return m ? m[1] : String(dateStr);
}

/* ======================================================
   CLASSIC VIEW  —  the Client Financial Summary
   Four letter pages, each a structural part that opens a printed sheet: the
   balance sheet, then cash through insurance, then income through notes, then
   the client profile. Modelled on "PQ Output Clean" (Jimmy, 2026-09-29): a
   branded header and five headline figures, then cards with a blue title bar
   and quiet, rule-separated tables.
   ====================================================== */

const CL = {
  blue: '#015697',
  navy: '#0b3a66',
  ink: '#1a2433',
  muted: '#6b7688',
  faint: '#b3bac6',
  rule: '#e3e8ef',
  headRule: '#cfd9e6',
  head: '#e8eef6',
  total: '#f3f6fa',
  tile: '#f4f7fb',
  card: '#dde3ea',
  green: '#1f7a4d',
  red: '#b3382b',
};

// The paper drop-shadow (the "sheet on a desk" look) is applied via a
// screen-only CSS rule (.pres-page in presentation.css), NOT inline here — an
// inline box-shadow survives into Chrome's print/PDF as a gray halo down the
// right and bottom edges, making each sheet read as a page-on-a-page.
//
// 1000 x 1294 is a letter sheet at the design's width. The height is a screen
// minimum only, so the page footer sits at the foot of the sheet; print drops
// it and lets the part take the height its content needs.
const PAGE = {
  width: 1000, minHeight: 1294, margin: '0 auto 36px', background: '#fff',
  padding: '52px 64px 34px', boxSizing: 'border-box', fontFamily: BODY,
  display: 'flex', flexDirection: 'column',
};
// Pages shown in the side-by-side pair: same as PAGE but no horizontal
// auto-margin (the flex container centers the pair) and no shrinking.
const PAGE_PAIR = { ...PAGE, margin: '0 0 36px', flexShrink: 0 };

const INSURANCE_NONE = {
  Life: 'life insurance',
  LTC: 'long-term care insurance',
  Disability: 'disability insurance',
  Umbrella: 'umbrella insurance',
  'P&C': 'property & casualty insurance',
};

// Estate documents are stored under short codes (pqSections estatePlan).
const ESTATE_LABELS = { Trust: 'Trust', Will: 'Will', FPOA: 'Financial POA', MPOA: 'Medical POA' };

function classicCell(v) {
  return (v == null || v === '') ? '—' : v;
}

// Dates are held as YYYY-MM-DD (the HTML date input's value format, and what
// Salesforce returns), which must never reach a client-facing page raw. Render
// US style instead. Tolerates a trailing time component, since a Salesforce
// datetime can arrive as YYYY-MM-DDTHH:MM:SS. Anything that is not an ISO date
// passes through untouched, so free text an advisor typed still survives.
function fmtDate(v) {
  if (v == null || v === '') return v;
  const s = String(v).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : s;
}

// classicCell + fmtDate together: US-formatted, with the em dash for blanks.
function dateCell(v) {
  return classicCell(fmtDate(v));
}

const pctCell = v => (v != null && String(v).trim() !== '' ? `${v}%` : '—');

// "$3.34M", "$540K": the headline tile's one-line reconciliation of net worth.
export function compactDollars(n) {
  const v = Math.abs(num(n));
  const sign = num(n) < 0 ? '-' : '';
  if (v >= 1e6) return `${sign}$${(v / 1e6).toFixed(2).replace(/\.?0+$/, '')}M`;
  if (v >= 1e3) return `${sign}$${Math.round(v / 1e3)}K`;
  return `${sign}$${Math.round(v)}`;
}

// "Michael & Nina Smith" when they share a surname, as the summary is headed.
export function householdName(c1First, c1Last, c2First, c2Last) {
  const one = [c1First, c1Last].filter(Boolean).join(' ');
  const two = [c2First, c2Last].filter(Boolean).join(' ');
  if (!two) return one || 'Client';
  if (c1First && c2First && c1Last && c1Last === c2Last) return `${c1First} & ${c2First} ${c1Last}`;
  return `${one || 'Client'} & ${two}`;
}

// What the headline income is made of, in the order the income section lists it.
export function incomeSources(incomeRows) {
  const parts = [
    ['employment', 'Employment'], ['socialSecurity', 'Social Security'],
    ['pension', 'Pension'], ['other', 'Other'],
  ].filter(([k]) => (incomeRows[k] || []).some(r => num(r.annualAmount) > 0)).map(([, label]) => label);
  return parts.length ? `${parts.join(', ')}, current` : 'None being received';
}

// The unrealized gain line under the taxable table, over the accounts that
// carry a cost basis. An account without one is left out rather than counted
// at a basis of zero, which would overstate the gain by its whole balance.
export function unrealizedGainNote(rows) {
  const priced = rows.filter(r => num(r.costBasis) > 0);
  if (!priced.length) return '';
  const mv = sumA(priced, 'marketValue');
  const cb = sumA(priced, 'costBasis');
  const gain = mv - cb;
  const scope = priced.length < rows.length ? ', on the accounts with a cost basis' : '';
  return `Unrealized ${gain >= 0 ? 'gain' : 'loss'} on taxable holdings: ${formatDollars(Math.abs(gain))} (${formatDollars(mv)} market value less ${formatDollars(cb)} cost basis${scope}).`;
}

// The Social Security totals row: "Combined at Age 67" when every row starts at
// the same age, which is what makes the sum a single figure a client receives.
export function socialSecurityTotalLabel(rows) {
  const ages = [...new Set(rows.map(r => String(r.startingAge ?? '').trim()).filter(Boolean))];
  const everyRow = rows.every(r => String(r.startingAge ?? '').trim());
  return ages.length === 1 && everyRow ? `Combined at Age ${ages[0]}` : 'Combined';
}

// Estate plan as a line of text. "None" is an ANSWER, not an absence, so it
// reads as one; a household nobody asked reads "Not provided".
export function estatePlanText(estatePlan) {
  const list = estateDocCodes(estatePlan);
  if (list.includes('None')) return 'None (asked, client has no estate documents)';
  const held = list.map(v => ESTATE_LABELS[v] || v).filter(Boolean);
  return held.join(' · ');
}

// Insured and owner print as an initial with a legend beneath, the way the
// summary saves the width, but only when each client's initial is distinct:
// "M" for both Michael and Mary would be worse than the names.
function initialsFor(c) {
  const first1 = (c.c1First || c.c1Last || '').trim();
  const first2 = (c.c2First || c.c2Last || '').trim();
  const i1 = first1.charAt(0).toUpperCase();
  const i2 = first2.charAt(0).toUpperCase();
  const distinct = i1 && (!first2 || (i2 && i1 !== i2));
  const used = new Map();
  const cell = v => {
    if (!distinct || v == null || String(v).trim() === '') return classicCell(v);
    const who = ownerPerson(c.family, v);
    if (who === 'client1') { used.set(i1, first1); return i1; }
    if (who === 'client2' && i2) { used.set(i2, first2); return i2; }
    return v;
  };
  return { cell, legend: () => [...used.entries()] };
}

/* ---- Building blocks ---- */

const TH = align => ({
  background: CL.head, color: CL.navy, font: `700 9px ${BODY}`, letterSpacing: '.1em',
  textTransform: 'uppercase', padding: '7px 8px', textAlign: align || 'left',
  borderBottom: `1px solid ${CL.headRule}`, verticalAlign: 'bottom',
});
const TDS = (align, first) => ({
  padding: '6px 8px', font: `${first ? 600 : 400} 12.5px ${BODY}`, color: CL.ink,
  textAlign: align || 'left', borderBottom: `1px solid ${CL.rule}`,
});
const TOTAL_TD = align => ({
  padding: '6px 8px', font: `700 12.5px ${BODY}`, color: CL.navy, textAlign: align || 'left',
  background: CL.total, borderTop: `2px solid ${CL.blue}`,
});

// cols=[{label,key,align,w,render,total}]. A column carrying `total` produces
// the totals row; `emptyTotals` keeps it on a table with no rows, where a $0
// total is a real answer (no business interests, no other debt).
function STable({ cols, rows, totalLabel = 'Total', emptyTotals = false, extraRow = null, mb = 0 }) {
  const hasTotals = cols.some(col => col.total != null) && (rows.length > 0 || emptyTotals);
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: mb }}>
      <thead>
        <tr>{cols.map((col, i) => <th key={i} style={{ ...TH(col.align), width: col.w }}>{col.label}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri}>
            {cols.map((col, ci) => (
              <td key={ci} style={TDS(col.align, ci === 0 && !col.plain)}>{col.render ? col.render(r) : classicCell(r[col.key])}</td>
            ))}
          </tr>
        ))}
        {rows.length === 0 && (
          <tr><td colSpan={cols.length} style={{ ...TDS('left'), fontStyle: 'italic', color: CL.muted }}>None reported</td></tr>
        )}
        {extraRow && (
          <tr><td colSpan={cols.length} style={{ ...TDS('left'), fontStyle: 'italic', color: CL.muted }}>{extraRow}</td></tr>
        )}
        {hasTotals && (
          <tr>
            {cols.map((col, ci) => {
              if (ci === 0) return <td key={ci} style={TOTAL_TD('left')}>{totalLabel}</td>;
              return <td key={ci} style={TOTAL_TD(col.align)}>{col.total != null ? col.total : ''}</td>;
            })}
          </tr>
        )}
      </tbody>
    </table>
  );
}

// A card with the blue title bar. `sec` makes it its own print section (kept
// whole on a sheet); cards laid out side by side share one section instead.
function Card({ title, children, sec = true, pad = '12px 14px 14px' }) {
  return (
    <div className={sec ? 'pres-sec pres-card' : 'pres-card'} style={{ border: `1px solid ${CL.card}`, borderRadius: 3, background: '#fff' }}>
      <div className="pres-card-head" style={{ background: CL.blue, color: '#fff', font: `700 12.5px ${BODY}`, letterSpacing: '.16em', textTransform: 'uppercase', padding: '9px 14px', borderRadius: '2px 2px 0 0' }}>{title}</div>
      <div className="pres-card-body" style={{ padding: pad }}>{children}</div>
    </div>
  );
}

function SubHead({ children, first }) {
  return (
    <div className={first ? undefined : 'pres-subhead'} style={{ font: `700 11px ${BODY}`, letterSpacing: '.12em', textTransform: 'uppercase', color: CL.navy, borderBottom: `1px solid ${CL.headRule}`, paddingBottom: 5, margin: first ? '0 0 8px' : '14px 0 8px' }}>{children}</div>
  );
}

function Kpi({ label, value, sub, dark }) {
  return (
    <div className="pres-kpi" style={{
      background: dark ? CL.blue : CL.tile, border: `1px solid ${dark ? CL.blue : CL.card}`,
      borderTop: `4px solid ${CL.blue}`, borderRadius: 3, padding: '11px 14px 13px',
    }}>
      <div style={{ font: `600 9px ${BODY}`, letterSpacing: '.12em', textTransform: 'uppercase', color: dark ? 'rgba(255,255,255,.82)' : CL.muted }}>{label}</div>
      <div className="pres-kpi-value" style={{ font: `700 22px ${BODY}`, color: dark ? '#fff' : CL.navy, margin: '5px 0 3px', whiteSpace: 'nowrap' }}>{value}</div>
      <div style={{ font: `400 10.5px ${BODY}`, color: dark ? 'rgba(255,255,255,.82)' : CL.muted }}>{sub}</div>
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div style={{ border: `1px solid ${CL.card}`, borderLeft: `4px solid ${color || CL.blue}`, borderRadius: 3, padding: '9px 14px 10px' }}>
      <div style={{ font: `600 9px ${BODY}`, letterSpacing: '.12em', textTransform: 'uppercase', color: CL.muted }}>{label}</div>
      <div style={{ font: `700 19px ${BODY}`, color: color || CL.navy, marginTop: 3 }}>{value}</div>
    </div>
  );
}

function Field({ label, value, span, pre }) {
  const empty = value == null || String(value).trim() === '';
  return (
    <div style={{ gridColumn: span ? `span ${span}` : undefined }}>
      <div style={{ font: `600 9px ${BODY}`, letterSpacing: '.12em', textTransform: 'uppercase', color: CL.muted, marginBottom: 4 }}>{label}</div>
      <div style={{ font: `${empty ? 400 : 700} 13px ${BODY}`, color: empty ? CL.faint : CL.ink, whiteSpace: pre ? 'pre-wrap' : undefined, lineHeight: 1.4 }}>{empty ? 'Not provided' : value}</div>
    </div>
  );
}

// Space to write in during the meeting, and nothing else. See the Additional
// Notes card for why that one must never print a stored field.
function WriteBox({ height }) {
  return <div style={{ border: '1.5px dashed #cfd9e6', borderRadius: 3, background: '#fbfcfe', height }} />;
}

function PageFoot({ name, n, of }) {
  return (
    <div className="pres-page-foot no-print" style={{ marginTop: 'auto', paddingTop: 26, display: 'flex', justifyContent: 'space-between', font: `400 10.5px ${BODY}`, color: CL.faint }}>
      <span>{name} | Confidential</span>
      <span>Pure Financial Advisors, LLC</span>
      <span>Page {n} of {of}</span>
    </div>
  );
}

// The printed footer, on every sheet a part flows onto. A page margin box is
// the only thing that repeats per printed sheet and knows its number: the
// screen footer above sits at the end of a part, and a part can run past one
// sheet. The household name has to reach the stylesheet, so it is written as
// a CSS string, escaped.
export function printFooterCss(name) {
  const safe = String(name || '').replace(/[\\"]/g, '\\$&').replace(/[\r\n]+/g, ' ');
  const box = `font: 400 7.5pt 'Source Sans 3', sans-serif; color: #9aa3b2; vertical-align: middle;`;
  return `@media print { @page { margin: 0.4in 0;
  @bottom-left { content: "${safe} | Confidential"; padding-left: 0.55in; ${box} }
  @bottom-center { content: "Pure Financial Advisors, LLC"; ${box} }
  @bottom-right { content: "Page " counter(page) " of " counter(pages); padding-right: 0.55in; ${box} }
} }`;
}

function ClassicView({ ctx, sideBySide = false }) {
  const c = ctx;
  const fmt = formatDollars;
  const name = householdName(c.c1First, c.c1Last, c.c2First, c.c2Last);
  const PAGES = 4;

  const reRows = cleanRows(c.assets.realEstate);
  const tdRows = cleanRows(c.assets.taxDeferred);
  const rothRows = cleanRows(c.assets.roth);
  const txRows = cleanRows(c.assets.taxable);
  const cashRows = cleanRows(c.assets.cashCd);
  // HSA rows are labeled by custodian since the Roth-style column change;
  // map it into description so the shared Business & Other table shows a name.
  const busRows = [
    ...cleanRows(c.assets.businessOther),
    ...cleanRows(c.assets.hsa).map(r => ({ ...r, description: r.description || r.custodian })),
    ...cleanRows(c.assets.plan529),
  ];
  const busTotal = sumA(busRows, 'marketValue');
  // Classic-form semantics: real-estate debt lives in the Real Estate table's
  // Loan Balance column, so "Other Liabilities" lists only non-RE debt — a
  // pilot advisor read the mortgage appearing in both tables as
  // double-counting. reLoanLines are RE debt by definition, and derived
  // mortgage rows are recognized with the same matching the net-worth engine
  // uses; Net Worth (c.netWorth) still counts every liability.
  const liabRows = cleanRows(c.liabs.items).filter(r => !isRealEstateLoanRow(r, c.assets));
  const insRows = cleanRows(c.ins.policies);
  const initials = initialsFor(c);
  // Resolved here, before render, so the legend beneath the table knows which
  // initials the table will use: the table's cells render after this function
  // returns, too late for a legend built from them.
  insRows.forEach(r => { initials.cell(r.insured); initials.cell(r.owner); });
  const insNone = c.insNoneOf.map(k => `No ${INSURANCE_NONE[k] || k}`).join('  ·  ');
  // The summary is the questionnaire record: list EVERY entered income row,
  // including not-yet-claimed Social Security and future-dated pensions —
  // that's client data the advisor typed, and the Starting Age / Start Date
  // columns say when it begins (UAT: a pilot advisor's future-age SS row showed
  // "None recorded"). Only the "Current Total" stays active-only
  // (c.totalIncome), so it still matches the headline and the edit rail.
  const empRows = cleanRows(c.income.employment);
  // Retirement Date on the presented sheet. The PQ accepts EITHER a retirement
  // date or a retirement age, so a household captured by age left this column
  // empty: "since we have the option of retirement age or retirement date,
  // either should flow through" (Jake, 2026-08-05). The age lives on the
  // Employment record rather than the income row, so match the row's owner
  // back to the client or spouse. Shown as "Age 63" rather than inventing a
  // date off the DOB.
  const empRetireCell = r => {
    if (String(r?.retirementDate || '').trim()) return dateCell(r.retirementDate);
    const emp = c.emp || {};
    const who = ownerPerson(c.family, r?.owner);
    const age = String((who ? emp[who]?.retirementAge : '') ?? '').trim();
    return age ? `Age ${age}` : '—';
  };
  const ssRows = cleanRows(c.income.socialSecurity);
  const penRows = cleanRows(c.income.pension);
  const otherRows = cleanRows(c.income.other);
  const proRows = cleanRows(c.rels.professionals);

  // The expense table opens with an empty starter row; untouched, it is not a
  // line item and printed as a row of dashes with a $0 amount.
  const typedExpRows = c.expRows.filter(r =>
    String(r.description ?? '').trim() || num(r.amount) || String(r.notes ?? '').trim()
  );
  const expDisplayRows = c.livingExpField > 0
    ? [{ description: 'Living Expenses', amount: c.livingExpField, notes: c.livingExpNote }, ...typedExpRows]
    : typedExpRows;
  // Living Expenses is a scalar field with no start date, so it always counts;
  // the table rows are filtered to the ones that have actually started.
  const expTotal = expDisplayRows.reduce(
    (s, r) => s + (r._uid == null || c.activeExpKeys.has(r._uid) ? num(r.amount) : 0),
    0
  );

  // Occupation shows the fields that match each person's employment status.
  // Toggling status keeps both sets in the store (a mis-click is recoverable),
  // so the display must pick the status-appropriate value, not just the first
  // truthy one — otherwise a retired client's old employer showed as their
  // "last employer" (John, 7/10). The PQ takes a retirement date OR a
  // retirement age, so a household captured by age shows the age (Marcus
  // Gonzalez, 2026-08-19).
  const occupationRow = (who, e = {}) => {
    const working = e.status !== 'Retired' && e.status !== 'Not Employed';
    return {
      who,
      jobTitle: working ? (e.jobTitle || e.lastJob) : (e.lastJob || e.jobTitle),
      employer: working ? (e.employer || e.lastEmployer) : (e.lastEmployer || e.employer),
      years: working ? (e.numOfYears || e.yearsWorked) : (e.yearsWorked || e.numOfYears),
      retire: fmtDate(e.retirementDate) || (String(e.retirementAge ?? '').trim() ? `Age ${String(e.retirementAge).trim()}` : ''),
    };
  };
  const occRows = [occupationRow(c.c1First || c.c1Name, c.emp.client1)];
  if (c.c2Name) occRows.push(occupationRow(c.c2First || c.c2Name, c.emp.client2));
  for (const b of otherBusinessLines(c)) {
    occRows.push({ who: b.whoFirst, jobTitle: b.role, employer: b.name, years: b.years, retire: '' });
  }

  const familyRows = [{
    first: c.c1First, last: c.c1Last, nick: c.family.client1Nickname,
    dob: fmtDate(c.family.client1DOB), age: c.family.client1Age, rel: 'Client',
  }];
  if (c.c2Name) {
    familyRows.push({
      first: c.c2First, last: c.c2Last, nick: c.family.client2Nickname,
      dob: fmtDate(c.family.client2DOB), age: c.family.client2Age,
      rel: 'Spouse' + (String(c.family.yearsMarried ?? '').trim() ? ` · Married ${c.family.yearsMarried} yrs` : ''),
    });
  }
  // "3 — Emma (12), Jack (9)" when itemized; the count alone when only the
  // number was entered, which advisors moving fast often do.
  const kin = (count, rows) => {
    if (!count) return '';
    const named = rows.map(r => {
      const n = String(r.name ?? '').trim();
      const a = String(r.age ?? '').trim();
      if (n && a) return `${n} (${a})`;
      return n || (a ? `age ${a}` : '');
    }).filter(Boolean);
    return named.length ? `${count} — ${named.join(', ')}` : String(count);
  };
  const goals = c.goalsText ? c.goalsText.split(/\r?\n+/).map(s => s.trim()).filter(Boolean) : [];
  const estate = estatePlanText(c.epData.estatePlan);
  const pensionOtherTogether = penRows.length === 0 && otherRows.length === 0;

  return (
    <div className="pres-classic" style={{ padding: '40px 36px 48px', background: '#d8dade' }}>
      <style>{printFooterCss(name)}</style>

      {/* Pages 1 & 2 stack by default; the toolbar toggle switches to
          side-by-side. Either way they stack for print. */}
      <div className={'pres-classic-pair' + (sideBySide ? ' side-by-side' : '')}>

      {/* ===== PAGE 1 · HEADLINE AND INVESTMENTS =====
          Each section sits in its own .pres-sec so the PRINT stylesheet can
          keep it whole: a section that does not fit the remaining sheet moves
          to the next sheet intact, at 100% scale, instead of the whole page
          being zoomed down to fit. On screen .pres-sec is inert. */}
      <div className="pres-page" style={PAGE_PAIR}>
        <div className="pres-sec">
          <div style={{ borderTop: `7px solid ${CL.blue}`, paddingTop: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24 }}>
            <div>
              <div style={{ font: `700 25px ${BODY}`, letterSpacing: '.05em', color: CL.blue, lineHeight: 1 }}>
                PURE <span style={{ fontWeight: 400, color: CL.muted }}>FINANCIAL ADVISORS</span>
              </div>
              <div style={{ font: `600 9.5px ${BODY}`, letterSpacing: '.22em', textTransform: 'uppercase', color: CL.muted, marginTop: 9 }}>
                Fee-Only Fiduciary · Financial Planning &amp; Wealth Management
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ font: `700 27px ${BODY}`, color: CL.blue, lineHeight: 1.05 }}>Client Financial Summary</div>
              <div style={{ font: `400 13px ${BODY}`, color: CL.muted, marginTop: 6 }}>
                <b style={{ color: '#4a5568' }}>{name}</b>  ·  Prepared {c.asOf}
              </div>
            </div>
          </div>
          <div className="pres-head-rule" style={{ borderTop: `1px solid ${CL.rule}`, margin: '16px 0 16px' }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
            <Kpi dark label="Total Net Worth" value={fmt(c.netWorth)} sub={`${compactDollars(c.totalAssets)} assets − ${compactDollars(c.totalLiab)} debt`} />
            <Kpi label="Investable Assets" value={fmt(c.investable)} sub="Retirement, Roth, taxable & cash" />
            <Kpi label="Annual Income" value={fmt(c.totalIncome)} sub={incomeSources(c.incomeRows)} />
            <Kpi label="Annual Savings" value={fmt(c.totalSavings)} sub={`${c.savingsRatio.toFixed(2)}% savings ratio`} />
            <Kpi label="Net Cash Flow" value={(c.netCF >= 0 ? '+' : '') + fmt(c.netCF)} sub="After tax, expenses & savings" />
          </div>
        </div>

        <Card title="Real Estate Assets">
          <STable
            cols={[
              { label: 'Description', key: 'description', w: '19%' },
              { label: 'Market Value', align: 'right', render: r => fmt(num(r.marketValue)), total: fmt(c.reAssets) },
              { label: 'Loan Balance', align: 'right', render: r => money0(r.remainingLoan), total: money0(sumA(reRows, 'remainingLoan')) },
              { label: 'Int. Rate', align: 'center', render: r => pctCell(r.interestRate) },
              { label: 'Term', align: 'center', key: 'term' },
              { label: 'Pmt (P&I)', align: 'right', render: r => money0(r.payment), total: money0(sumA(reRows, 'payment')) },
              { label: 'Income EBT', align: 'right', render: r => money0(r.incomeEBT), total: money0(sumA(reRows, 'incomeEBT')) },
              { label: 'Acq. Year', align: 'center', key: 'yearAcquired' },
              { label: 'Cost Basis', align: 'right', render: r => money0(r.costBasis) },
              { label: 'Ownership', key: 'ownership' },
            ]}
            rows={reRows}
          />
        </Card>

        <Card title="Tax-Deferred Retirement Accounts">
          <STable
            cols={[
              { label: 'Custodian', w: '26%', render: r => classicCell(r.custodian || r.description) },
              { label: 'Market Value', align: 'right', render: r => fmt(num(r.marketValue)), total: fmt(c.taxDef) },
              { label: 'Personal Additions', align: 'right', render: r => money0(r.personalAdditions), total: money0(sumA(tdRows, 'personalAdditions')) },
              { label: 'Company Match', align: 'right', render: r => money0(r.companyMatch), total: money0(sumA(tdRows, 'companyMatch')) },
              { label: 'Type', key: 'type' },
              { label: 'Ownership', key: 'ownership' },
              { label: 'Beneficiary', key: 'beneficiary' },
            ]}
            rows={tdRows}
          />
        </Card>

        <Card title="Tax-Free Roth Accounts">
          <STable
            cols={[
              { label: 'Custodian', w: '26%', render: r => classicCell(r.custodian || r.description) },
              { label: 'Market Value', align: 'right', render: r => fmt(num(r.marketValue)), total: fmt(c.rothVal) },
              { label: 'Personal Additions', align: 'right', render: r => money0(r.personalAdditions), total: money0(sumA(rothRows, 'personalAdditions')) },
              { label: 'Company Match', align: 'right', render: r => money0(r.companyMatch), total: money0(sumA(rothRows, 'companyMatch')) },
              { label: 'Roth Type', key: 'type' },
              { label: 'Ownership', key: 'ownership' },
              { label: 'Beneficiary', key: 'beneficiary' },
            ]}
            rows={rothRows}
          />
        </Card>

        <Card title="Taxable Non-Retirement Accounts">
          <STable
            cols={[
              { label: 'Custodian', w: '22%', render: r => classicCell(r.custodian || r.description) },
              { label: 'Market Value', align: 'right', render: r => fmt(num(r.marketValue)), total: fmt(c.taxableVal) },
              { label: 'Additions', align: 'right', render: r => money0(r.personalAdditions), total: money0(sumA(txRows, 'personalAdditions')) },
              { label: 'Cost Basis', align: 'right', render: r => money0(r.costBasis), total: money0(sumA(txRows, 'costBasis')) },
              { label: 'Ownership', key: 'ownership' },
              { label: 'Variable / Fixed', key: 'variableFixed' },
              { label: 'Issue Date', render: r => dateCell(r.issueDate) },
              { label: 'Beneficiary', key: 'beneficiary' },
            ]}
            rows={txRows}
          />
          {unrealizedGainNote(txRows) && (
            <div style={{ font: `400 11px ${BODY}`, color: CL.muted, marginTop: 10 }}>{unrealizedGainNote(txRows)}</div>
          )}
        </Card>
        <PageFoot name={name} n={1} of={PAGES} />
      </div>

      {/* ===== PAGE 2 · CASH, OTHER HOLDINGS, DEBT, INSURANCE ===== */}
      <div className="pres-page" style={PAGE_PAIR}>
        <div className="pres-sec" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, alignItems: 'start' }}>
          <Card title="Cash & CD's" sec={false}>
            <STable
              cols={[
                { label: 'Description', w: '44%', key: 'description' },
                { label: 'Market Value', align: 'right', render: r => fmt(num(r.marketValue)), total: fmt(c.cashCd) },
                { label: 'Int. Rate', align: 'center', render: r => pctCell(r.interestRate) },
                { label: 'Owner', key: 'ownership' },
              ]}
              rows={cashRows}
              emptyTotals
            />
          </Card>
          <Card title="Business & Other" sec={false}>
            <STable
              cols={[
                { label: 'Description', w: '40%', key: 'description' },
                { label: 'Market Value', align: 'right', render: r => fmt(num(r.marketValue)), total: fmt(busTotal) },
                { label: 'Cost Basis', align: 'right', render: r => money0(r.costBasis), total: fmt(sumA(busRows, 'costBasis')) },
                { label: 'Owner', key: 'ownership' },
              ]}
              rows={busRows}
              emptyTotals
            />
          </Card>
        </div>

        <Card title="Other Liabilities">
          <STable
            cols={[
              { label: 'Description', w: '40%', key: 'description' },
              { label: 'Payment', align: 'right', render: r => money0(r.payment), total: fmt(sumA(liabRows, 'payment')) },
              { label: 'Amount', align: 'right', render: r => fmt(num(r.amount)), total: fmt(sumA(liabRows, 'amount')) },
              { label: 'Int. Rate', align: 'center', render: r => pctCell(r.interestRate) },
              { label: 'Term', align: 'center', key: 'term' },
            ]}
            rows={liabRows}
            emptyTotals
          />
        </Card>

        <Card title="Insurance Information">
          {/* The "asked, none" answers sit in the table as their own line.
              Without them the sheet is silent about coverage the advisor DID
              ask about, which is the whole reason advisors wanted somewhere to
              record it. */}
          <STable
            cols={[
              { label: 'Company', w: '16%', key: 'company' },
              { label: 'Type', key: 'type' },
              { label: 'Death / Daily Benefit', align: 'right', render: r => money0(r.benefit), total: fmt(sumA(insRows, 'benefit')) },
              { label: 'Insured', render: r => initials.cell(r.insured) },
              { label: 'Owner', render: r => initials.cell(r.owner) },
              { label: 'Policy Date', render: r => dateCell(r.policyDate) },
              { label: 'Annual Premium', align: 'right', render: r => money0(r.annualPremium), total: fmt(sumA(insRows, 'annualPremium')) },
              { label: 'Cash Value', align: 'right', render: r => money0(r.cashValue), total: fmt(sumA(insRows, 'cashValue')) },
              { label: 'Beneficiary', key: 'beneficiary' },
            ]}
            rows={insRows}
            extraRow={insNone || null}
          />
          {initials.legend().length > 0 && (
            <div style={{ font: `400 10.5px ${BODY}`, color: CL.muted, marginTop: 8 }}>
              {initials.legend().map(([i, who], k) => (
                <span key={i}>{k > 0 ? '  ·  ' : ''}<b style={{ color: '#4a5568' }}>{i}</b> = {who}</span>
              ))}
            </div>
          )}
        </Card>
        <PageFoot name={name} n={2} of={PAGES} />
      </div>
      </div>{/* end pres-classic-pair */}

      {/* ===== PAGE 3 · INCOME, TAX, EXPENSES, NOTES ===== */}
      <div className="pres-page" style={PAGE}>
        <Card title="Income & Tax Information">
          <SubHead first>Employment Income</SubHead>
          <STable
            cols={[
              { label: 'Person', w: '19%', key: 'owner' },
              { label: 'Description', key: 'description' },
              { label: 'Annual Amount', align: 'right', render: r => fmt(num(r.annualAmount)) },
              { label: 'Retirement Date', render: empRetireCell },
              { label: 'COLA %', align: 'right', render: r => pctCell(r.cola) },
            ]}
            rows={empRows}
          />

          <SubHead>Social Security</SubHead>
          <STable
            cols={[
              { label: 'Person', w: '19%', key: 'owner' },
              { label: 'Starting Age', align: 'right', key: 'startingAge' },
              { label: 'Annual Amount', align: 'right', render: r => fmt(num(r.annualAmount)), total: fmt(sumA(ssRows, 'annualAmount')) },
              { label: 'Full Retirement Age', align: 'right', key: 'fullRetAge' },
              { label: 'COLA %', align: 'right', render: r => pctCell(r.cola) },
            ]}
            rows={ssRows}
            totalLabel={socialSecurityTotalLabel(ssRows)}
          />

          {/* Side by side only while both are empty, as the summary lays them
              out; with rows in either, each needs the full width to name its
              person and description (Jeff Wille, 2026-08-19: three pensions
              read as three identical owner rows without the description). */}
          <div style={pensionOtherTogether ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 } : undefined}>
            <div>
              <SubHead>Pension</SubHead>
              <STable
                cols={pensionOtherTogether ? [
                  { label: 'Start Date', plain: true },
                  { label: 'Annual Amount', align: 'right' },
                  { label: 'Survivor Benefit', align: 'right' },
                  { label: 'COLA %', align: 'right' },
                ] : [
                  { label: 'Person', w: '19%', key: 'owner' },
                  { label: 'Description', key: 'description' },
                  // A pension can be entered by start date or start age; show
                  // whichever the advisor filled in.
                  { label: 'Start', render: r => fmtDate(r.startDate) || (r.startAge ? `Age ${r.startAge}` : '—') },
                  { label: 'Annual Amount', align: 'right', render: r => fmt(num(r.annualAmount)) },
                  { label: 'Survivor Benefit', align: 'right', render: r => pctCell(r.survivorBenefit) },
                  { label: 'COLA %', align: 'right', render: r => pctCell(r.cola) },
                ]}
                rows={penRows}
              />
            </div>
            <div>
              <SubHead>Other Income</SubHead>
              <STable
                cols={pensionOtherTogether ? [
                  { label: 'Description', plain: true },
                  { label: 'Amount', align: 'right' },
                  { label: 'Start Date' },
                  { label: 'End Date' },
                  { label: 'COLA %', align: 'right' },
                ] : [
                  { label: 'Person', w: '19%', key: 'owner' },
                  { label: 'Description', key: 'description' },
                  { label: 'Amount', align: 'right', render: r => fmt(num(r.annualAmount)) },
                  { label: 'Start Date', render: r => dateCell(r.startDate) },
                  { label: 'End Date', render: r => dateCell(r.endDate) },
                  { label: 'COLA %', align: 'right', render: r => pctCell(r.cola) },
                ]}
                rows={otherRows}
              />
            </div>
          </div>

          {/* The household income total, standing on its own so it always
              renders: hung off a table it vanished whenever that table had no
              rows (Jake, 2026-08-05). Active-only (c.totalIncome), so it
              matches the Annual Income headline and the edit rail exactly. */}
          <div style={{ display: 'flex', justifyContent: 'space-between', background: CL.total, borderTop: `2px solid ${CL.blue}`, padding: '8px 8px', marginTop: 12, font: `700 12.5px ${BODY}`, color: CL.navy }}>
            <span>Current Total</span>
            <span>{fmt(c.totalIncome)}</span>
          </div>

          {/* The heading and the deduction follow what the advisor chose in
              Taxes & Expenses: a current-year estimate is titled as one, and a
              standard deduction, which has no amount, is named rather than
              printed as a dash (Tony Vu, 2026-09-28). */}
          <SubHead>{taxSummaryTitle(c.taxes)}</SubHead>
          <STable
            cols={[
              { label: 'Taxable Income', render: () => money0(c.taxes.taxableIncome) },
              { label: 'Std / Itemized Ded.', render: () => deductionText(c.taxes) },
              { label: 'Cap Loss Carry Forward', render: () => money0(c.taxes.capLossCarryForward) },
              { label: 'Federal Tax', align: 'right', render: () => money0(c.fedTax) },
              { label: 'State Tax', align: 'right', render: () => money0(c.stTax) },
              { label: 'FICA Tax', align: 'right', render: () => money0(c.ficaTax), total: fmt(c.totalTax) },
            ]}
            rows={[{}]}
            totalLabel="Total Taxes Paid"
          />
        </Card>

        {/* Expenses, the cash-flow figures and the estate plan read as one
            closing block, so they share a section. */}
        <Card title="Expenses & Liabilities">
          <STable
            cols={[
              { label: 'Description', w: '24%', key: 'description' },
              { label: 'Amount', align: 'right', render: r => fmt(num(r.amount)), total: fmt(expTotal) },
              { label: 'Start Date', render: r => dateCell(r.startDate) },
              { label: 'End Date', render: r => dateCell(r.endDate) },
              { label: 'COLA %', align: 'right', render: r => pctCell(r.cola) },
              { label: 'Notes', w: '24%', key: 'notes' },
            ]}
            rows={expDisplayRows}
            totalLabel="Current Total"
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, margin: '16px 0 16px' }}>
            <Metric label="Total Savings" value={fmt(c.totalSavings)} />
            <Metric label="Total Cash Flow +/−" value={(c.netCF >= 0 ? '+' : '') + fmt(c.netCF)} color={c.netCF >= 0 ? CL.green : CL.red} />
            <Metric label="Savings Ratio" value={c.savingsRatio.toFixed(2) + '%'} />
            <Metric label="Debt to Income Ratio" value={c.dtiRatio.toFixed(2) + '%'} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
            <Field label="Estate Plan" value={estate} />
            <Field label="Year Established / Last Updated" value={c.epData.yearsEstablished} />
          </div>
        </Card>

        {/* Space to write in during the meeting, and nothing else. This block
            used to print `additionalNotes`, and the note panel (sidebar tile +
            Ctrl+Q) was the ONLY thing that ever wrote that field. So every
            value in it was typed as private advisor shorthand, whatever the
            placeholder claimed — which means rendering it here leaked advisor
            notes onto the client's copy on every existing record. The panel
            now writes `advisorNotes` instead. `additionalNotes` is left unread
            rather than deleted: the historical values stay in Salesforce and
            are recoverable, they just never print. */}
        <Card title="Additional Notes">
          <WriteBox height={78} />
        </Card>
        <PageFoot name={name} n={3} of={PAGES} />
      </div>

      {/* ===== PAGE 4 · THE CLIENT PROFILE ===== */}
      <div className="pres-page" style={PAGE}>
        <Card title="Family Information">
          <STable
            cols={[
              { label: 'Name', key: 'first', w: '16%' },
              { label: 'Last Name', key: 'last' },
              { label: 'Nickname', key: 'nick' },
              { label: 'Birth Date', key: 'dob' },
              { label: 'Age', align: 'right', key: 'age' },
              { label: 'Relationship', key: 'rel', w: '28%' },
            ]}
            rows={familyRows}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1fr 1fr', gap: '16px 18px', marginTop: 16 }}>
            <Field label="Residence Address" value={[c.contact.address1, c.contact.address2].filter(Boolean).join(', ')} />
            <Field label="City" value={c.contact.city} />
            <Field label="State" value={c.contact.state} />
            <Field label="Zip Code" value={c.contact.zip} />
            <Field label="Referred By (Client Name / Source)" value={c.contact.referredBy} span={2} />
            <Field label="Children" value={kin(c.numKids, c.childRows)} />
            <Field label="Total Grandchildren" value={kin(c.numGrand, c.grandRows)} />
            {/* pre-wrap: the advisor's line breaks were collapsing into one
                paragraph on the presented sheet (Jake, 2026-08-05). */}
            <Field label="Additional Family Information" value={c.family.additionalFamilyInfo} span={4} pre />
          </div>
        </Card>

        <Card title="Occupation">
          <STable
            cols={[
              { label: 'Person', key: 'who', w: '16%' },
              { label: 'Job Title', key: 'jobTitle' },
              { label: 'Employer (Last, if Retired)', key: 'employer', w: '30%' },
              { label: '# of Years', align: 'right', key: 'years' },
              { label: 'Retirement Date', align: 'right', key: 'retire' },
            ]}
            rows={occRows}
          />
        </Card>

        <Card title="Goals">
          {goals.length > 0
            ? goals.map((g, i) => (
              <div key={i} style={{ display: 'flex', gap: 9, padding: '6px 2px', borderBottom: `1px solid ${CL.rule}`, font: `400 13px ${BODY}`, color: CL.ink, lineHeight: 1.45 }}>
                <span style={{ color: CL.blue, flex: 'none' }}>▪</span><span>{g}</span>
              </div>
            ))
            : <WriteBox height={112} />}
        </Card>

        {/* The disclosure closes the final section rather than trailing the
            document as a block of its own: one line on its own gets orphaned
            onto a final otherwise-blank sheet whenever the last section ends
            near the foot of a page. As part of the section it travels with it. */}
        <div className="pres-sec">
          <Card title="Current Professional Relationships" sec={false}>
            <STable
              cols={[
                { label: 'Role', key: 'role', w: '22%' },
                { label: 'Name', key: 'name' },
                { label: 'Firm Name', key: 'firm' },
                { label: 'City', key: 'city' },
                { label: 'State', key: 'state' },
              ]}
              rows={proRows}
            />
          </Card>
          <div className="pres-print-footer">
            <Disclosure />
          </div>
        </div>
        <PageFoot name={name} n={4} of={PAGES} />
      </div>
    </div>
  );
}

// A blank basis reads as prior year, the same default the Taxes & Expenses
// section shows, so a PQ saved before the choice existed prints as it did.
export function taxSummaryTitle(taxes) {
  return taxes.taxBasis === 'Current Year Estimate' ? 'Current Year Tax Estimate' : 'Prior Year Tax Summary';
}

// Standard has no amount to show, so it says so. Itemized shows the amount the
// advisor entered, or the word when they have not entered one yet. A PQ with no
// type recorded keeps printing whatever amount it carries.
export function deductionText(taxes) {
  if (taxes.deductionType === 'Standard') return 'Standard';
  if (taxes.deductionType === 'Itemized') {
    return num(taxes.standardItemDeduction) ? `Itemized ${money0(taxes.standardItemDeduction)}` : 'Itemized';
  }
  return money0(taxes.standardItemDeduction);
}
